import { Flex, FlexGrow } from '@equifinance/shared';
import styled from 'styled-components';
import { useTabNavHeight } from '../../AccountTabNav';
import { CreditAddress, addressTypeFromAddress } from '../../../contexts/credit';
import { usePageConfigurationContext } from '../../../contexts/pageConfiguration';
import { useProgressStatusByInitiator } from '../../../contexts/progressStatus/ProgressStatusProvider';

export const AddressContainer = styled(Flex)<{topOffset: number}>` ${({ theme, topOffset }) => ` 
  width:100%;
  position: sticky;
  top: ${topOffset}px;
  z-index: 1;
  height: ${theme.padding[9]};
`}`;

export const CurrentAddressTitleDefault = styled(Flex)` ${({ theme }) => `  
  align-items:center;
  color:${theme.colours.neutral[10]};
  font-size:${theme.fontSize[1]};
  background:${theme.colours.primary[2]};
  padding:${theme.padding[2]};
  padding-left:${theme.padding[4]};
  padding-right:${theme.padding[4]};
  font-weight:bold;
`}`;

export const CurrentAddressTitleGrey = styled(CurrentAddressTitleDefault)` ${({ theme }) => `  
  background:${theme.colours.neutral[4]};
`}`;

export const AddressTableTileDefault = styled(FlexGrow)` ${({ theme }) => `
  display:flex;
  align-items:center;
  color:${theme.colours.neutral[2]};
  font-size:${theme.fontSize[1]};
  background:${theme.colours.primary[8]};
  padding-left:${theme.padding[4]};
`}`;

export const AddressTableTileGrey = styled(AddressTableTileDefault)` ${({ theme }) => `
  background:${theme.colours.neutral[8]};
`}`;

export const CreditItemButtonsDefault = styled(Flex)<{disabled?: boolean}>` ${({ theme, disabled }) => `
  align-items:center;
  justify-content:end; 
  color:${theme.colours.primary[1]};
  font-weight:${theme.fontWeight.bolder};
  cursor: ${disabled ? 'default' : 'pointer'};
  height:  100%;
  font-size:${theme.fontSize[1]};
  padding-left: ${theme.padding[6]};
  padding-right: ${theme.padding[6]};
  &:hover {
    background: ${disabled ? 'none' : theme.colours.primary[7]};
  }
  &:active {
    background: ${disabled ? 'none' : theme.colours.primary[6]};
  };
`}`;

export const CreditItemButtonsGrey = styled(CreditItemButtonsDefault)` ${({ theme }) => `
  color:${theme.colours.neutral[2]};
`}`;

export const AddCreditIcon = styled.i.attrs(() => ({
  className: 'mi-add',
}))` ${({ theme }) => `
  font-size:${theme.fontSize[3]};
  padding-right: ${theme.padding[1]};
`}`;

export default function CreditTableAddress({
  count,
  address,
  onClickAddCreditItem,
  hasItems = true,
  id = '',
}: {
  count?: number,
  address: CreditAddress,
  onClickAddCreditItem: () => void,
  hasItems?: boolean,
  id?: string,
  }) {
  const addressType = addressTypeFromAddress(address);

  const capitalizedAddressType = addressType.charAt(0).toUpperCase() + addressType.slice(1);

  const {
    subBuilding, houseName, houseNumber, street1, street2, postTown, county, postcode,
  } = address;

  const addressText = [
    subBuilding, houseName, houseNumber, street1, street2, postTown, county, postcode,
  ].filter(Boolean).map((part) => part.trim()).join(', ');

  const CurrentAddressTitle = hasItems ? CurrentAddressTitleDefault : CurrentAddressTitleGrey;
  const AddressTableTile = hasItems ? AddressTableTileDefault : AddressTableTileGrey;
  const CreditItemButtons = hasItems ? CreditItemButtonsDefault : CreditItemButtonsGrey;

  const tabNavHeight = useTabNavHeight();

  const { isReadOnly } = usePageConfigurationContext();

  const addCreditItemInProgress = useProgressStatusByInitiator(id);

  const onAddCreditItem = () => {
    if (isReadOnly || addCreditItemInProgress) return;

    onClickAddCreditItem();
  };

  return (
    <AddressContainer topOffset={tabNavHeight}>
      <CurrentAddressTitle>
        {`${capitalizedAddressType} address${count ? ` ${count}` : ''}`}
      </CurrentAddressTitle>

      <AddressTableTile>
        <FlexGrow>
          {addressText}
        </FlexGrow>
        {!isReadOnly && (
        <CreditItemButtons disabled={isReadOnly || addCreditItemInProgress} onClick={onAddCreditItem} aria-hidden="true">
          <AddCreditIcon />
          Add
        </CreditItemButtons>
        )}

      </AddressTableTile>
    </AddressContainer>
  );
}
